import { useEffect, useState } from "react";
import Topbar from "../components/layout/Topbar.jsx";
import { useNotifications } from "../context/NotificationContext.jsx";
import {
  getAllNotifications,
  markAsRead,
  markAllAsRead,
} from "../services/notificationService";

// ─────────────────────────────────────────────────────────────────────────────
//  Mapping NotificationType (backend) → icône + couleur
//  Le backend envoie : INFO | WARNING | SUCCESS | ERROR
// ─────────────────────────────────────────────────────────────────────────────
const TYPE_STYLE = {
  INFO:    { icon: "bi-info-circle",          color: "teal"   },
  WARNING: { icon: "bi-exclamation-triangle", color: "orange" },
  SUCCESS: { icon: "bi-check2-circle",        color: "green"  },
  ERROR:   { icon: "bi-x-circle",             color: "red"    },
};

const COLOR_MAP = { teal: "var(--accent)", green: "var(--success)", orange: "var(--accent2)", red: "var(--danger)" };
const BG_MAP    = { teal: "var(--accent-dim)", green: "var(--success-dim)", orange: "var(--accent2-dim)", red: "var(--danger-dim)" };

function timeAgo(isoDate) {
  if (!isoDate) return "";
  const diff = Math.floor((Date.now() - new Date(isoDate)) / 1000 / 60);
  if (diff < 1)    return "à l'instant";
  if (diff < 60)   return `il y a ${diff}min`;
  if (diff < 1440) return `il y a ${Math.floor(diff / 60)}h`;
  return `il y a ${Math.floor(diff / 1440)}j`;
}

export default function NotificationsPage() {

  const [notifs,  setNotifs]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [filter,  setFilter]  = useState("all"); // "all" | "unread"

  // Fonctions du contexte pour synchroniser Topbar + Sidebar en temps réel
  const { decrementUnread, resetUnread } = useNotifications();

  // ── Chargement initial ────────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const res = await getAllNotifications();
        setNotifs(res.data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // ── Marquer une notif comme lue ───────────────────────────────────────
  const handleMarkAsRead = async (notif) => {
    if (notif.read) return;
    try {
      await markAsRead(notif.id);
      setNotifs(prev => prev.map(n => n.id === notif.id ? { ...n, read: true } : n));
      decrementUnread(1); // ← met à jour le badge Topbar/Sidebar immédiatement
    } catch (err) {
      console.error(err.message);
    }
  };

  // ── Marquer toutes les notifs comme lues ──────────────────────────────
  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead();
      setNotifs(prev => prev.map(n => ({ ...n, read: true })));
      resetUnread(); // ← remet le badge à 0 dans Topbar/Sidebar immédiatement
    } catch (err) {
      console.error(err.message);
    }
  };

  const unreadCount     = notifs.filter(n => !n.read).length;
  const displayedNotifs = filter === "unread" ? notifs.filter(n => !n.read) : notifs;

  if (loading) return (
    <>
      <Topbar title="Mes" subtitle="notifications" />
      <div className="page-body" style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 300 }}>
        <span style={{ color: "var(--text-3)", fontSize: 14 }}>Chargement…</span>
      </div>
    </>
  );

  if (error) return (
    <>
      <Topbar title="Mes" subtitle="notifications" />
      <div className="page-body" style={{ color: "var(--danger)", padding: 20 }}>{error}</div>
    </>
  );

  return (
    <>
      <Topbar title="Mes" subtitle="notifications" />
      <div className="page-body">

        {/* ── En-tête ── */}
        <div className="page-header">
          <div>
            <div className="page-title">
              Notifications{" "}
              {unreadCount > 0 && (
                <span style={{ fontSize: 14, color: "var(--accent)", fontWeight: 400 }}>
                  ({unreadCount} nouvelles)
                </span>
              )}
            </div>
          </div>
          <div className="page-actions">
            {unreadCount > 0 && (
              <button className="btn-itm btn-itm-outline" onClick={handleMarkAllAsRead}>
                <i className="bi bi-check-all" /> Tout marquer comme lu
              </button>
            )}
          </div>
        </div>

        {/* ── Filtres ── */}
        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          {[
            { key: "all",    label: "Toutes" },
            { key: "unread", label: `Non lues (${unreadCount})` },
          ].map(f => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              style={{
                padding: "5px 14px", borderRadius: 20, fontSize: 12,
                border: "1px solid var(--border)", cursor: "pointer",
                background: filter === f.key ? "var(--accent-dim)" : "transparent",
                color:      filter === f.key ? "var(--accent)"     : "var(--text-3)",
                fontWeight: filter === f.key ? 600 : 400, transition: "all .15s",
              }}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* ── Liste ── */}
        <div className="itm-card anim-fade-up">
          {displayedNotifs.length === 0 ? (
            <div className="empty-state">
              <i className="bi bi-bell-slash" />
              <p>{filter === "unread" ? "Aucune notification non lue." : "Aucune notification."}</p>
            </div>
          ) : (
            displayedNotifs.map((n, i) => {
              const style = TYPE_STYLE[n.type] ?? TYPE_STYLE.INFO;
              return (
                <div
                  key={n.id}
                  onClick={() => handleMarkAsRead(n)}
                  style={{
                    display: "flex", gap: 14, padding: "14px 0",
                    borderBottom: i < displayedNotifs.length - 1 ? "1px solid var(--border)" : "none",
                    opacity: n.read ? 0.7 : 1,
                    cursor:  n.read ? "default" : "pointer",
                    background: !n.read ? "rgba(0,194,203,.02)" : "transparent",
                    transition: "all .18s",
                  }}
                >
                  <div style={{ width: 40, height: 40, borderRadius: 12, background: BG_MAP[style.color], color: COLOR_MAP[style.color], display: "flex", alignItems: "center", justifyContent: "center", fontSize: 17, flexShrink: 0 }}>
                    <i className={`bi ${style.icon}`} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontWeight: n.read ? 400 : 600, fontSize: 14, color: "var(--text-1)" }}>
                        {n.title}
                      </span>
                      {!n.read && <div style={{ width: 8, height: 8, borderRadius: "50%", background: "var(--accent)", flexShrink: 0 }} />}
                    </div>
                    {n.message && (
                      <div style={{ fontSize: 12, color: "var(--text-3)", marginTop: 2 }}>{n.message}</div>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--text-3)", flexShrink: 0, marginTop: 2 }}>
                    {timeAgo(n.createdAt)}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </>
  );
}